import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { Box, Card, IconButton, StackRow } from '@dhl-ui/components'
import { formatShortDateWithTimeSeconds } from '@dhl/utils'
import { ImportIcon } from '@dhl-ui/icons'
import { useGetBlobsHook } from '@dhl/mdp-api'
import { AdminDataGrid, useNotificationMessage } from '~/common'
import { useCustomsFiles } from '~/modules/ppp/submodules/customs/hooks'
import { ClipboardCopy } from '~/modules/ppp/components/ClipboardCopy'
import type { CustomsFile } from '~/modules/ppp/submodules/customs/model'
import { downloadAnything } from '~/common/util'

type Props = {
  /**
   * Unix timestamp
   */
  fromDate: number
  /**
   * Unix timestamp
   */
  toDate: number
}

export const CustomsFiles = ({ fromDate, toDate }: Props) => {
  const { t } = useTranslation()
  const { data: files, isLoading } = useCustomsFiles(fromDate, toDate)
  const getBlobs = useGetBlobsHook()
  const { showError } = useNotificationMessage()

  const downloadFile = async (fileName: string, blobPath: string) => {
    return getBlobs({ filePath: blobPath })
      .then((blob) => {
        downloadAnything(fileName, blob, blob.type)
      })
      .catch(() => {
        showError(t('admin.pppPage.hits.downloadFailed', { fileName }))
      })
  }

  const renderCell = ({
    value = t('admin.pppPage.overview.unknownFile'),
  }: GridRenderCellParams<CustomsFile, CustomsFile[keyof CustomsFile]>) => (
    <StackRow>
      <Box>{value}</Box>
      <ClipboardCopy payload={`${value}`} />
    </StackRow>
  )

  const renderDownloadCell = ({ value, row }: GridRenderCellParams<CustomsFile, CustomsFile['blobPath']>) => (
    <IconButton
      aria-label={`${t('admin.pppCustomsPage.overview.download')} ${row.filename}`}
      onClick={() => value && downloadFile(row.filename, value)}
      size="small"
    >
      <ImportIcon />
    </IconButton>
  )

  const columns: GridColDef<CustomsFile>[] = [
    {
      field: 'blobPath',
      headerName: t('admin.pppCustomsPage.overview.download'),
      renderCell: renderDownloadCell,
    },
    {
      field: 'filename',
      headerName: t('admin.pppPage.overview.fileName'),
      flex: 1,
      valueGetter: (value) => value ?? t('admin.pppPage.overview.unknownFile'),
      renderCell,
    },
    {
      field: 'uploaded',
      headerName: t('admin.pppCustomsPage.overview.timestamp'),
      flex: 1,
      valueGetter: (value) => formatShortDateWithTimeSeconds(value),
      renderCell,
    },
  ]

  return (
    <Card>
      <Card.Content noPaddings>
        <AdminDataGrid columns={columns} rows={files} loading={isLoading} />
      </Card.Content>
    </Card>
  )
}
